import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { useGetUnitLoadHeightConsolidatedDataQuery } from '@/redux/slices/preship/preship-api-slice';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

interface ConsolidatedViewProps {
  isOpen?: boolean;
  onClose?: () => void;
  variant?: string;
  activeWeek?: string
}

const SummaryStackUnitLoadHeight: React.FC<ConsolidatedViewProps> = ({ variant,isSum,activeWeek }) => {
  const [activeTab, setActiveTab] = useState('raw');
  const [activeStatistic, setActiveStatistic] = useState('max');
  const { projectId } = useSelector((state: RootState) => state.define);
  const { data: consolidatedData, isLoading, error, refetch } = useGetUnitLoadHeightConsolidatedDataQuery({ projectId, variant,isSum,weekType:activeWeek }, { refetchOnMountOrArgChange: true });

  if (isLoading) return (
    <div className="space-y-6 w-full animate-pulse">
      {/* Statistics Table Skeleton */}
      <div className="border rounded-md overflow-hidden">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-6 p-3">
            {Array(6).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-2" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="grid grid-cols-6 gap-2 p-2 border-b last:border-0">
              {Array(6).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Additional Stats Table Skeleton */}
      <div className="border rounded-md overflow-hidden mt-6">
        <div className="border-b bg-gray-50">
          <div className="grid grid-cols-2 p-3">
            {Array(2).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full mx-2" />
            ))}
          </div>
        </div>
        <div className="p-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="grid grid-cols-2 gap-2 p-2 border-b last:border-0">
              {Array(2).fill(0).map((_, j) => (
                <Skeleton key={j} className="h-10 w-full" />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Chart Skeleton */}
      <div className="mt-6">
        <div className="flex space-x-2 mb-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-10 w-24" />
          ))}
        </div>
        <Skeleton className="h-[400px] w-full rounded-md" />
      </div>
    </div>
  );

  if (error) return <div>Error loading data</div>;

  const renderRawDataTable = () => (
    <Table className="border-collapse w-full">
      <TableHeader>
        <TableRow>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet Name</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet Type</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Week Type</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D</TableHead>
          <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {consolidatedData.rawData.map((row: any, index: number) => (
          <TableRow key={index} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
            <TableCell className="border border-gray-300 text-center p-2">{row.palletName}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row.weekType}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row['A-B']}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row['B-C']}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row['C-D']}</TableCell>
            <TableCell className="border border-gray-300 text-center p-2">{row['D-A']}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  const renderStatisticsTable = () => (
    <div className="space-y-6">
      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Statistic</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Pallet Type</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D (in)</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A (in)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {['Max', 'Min', 'Average', 'stdev', 'Count'].map((stat, index) => (
            <React.Fragment key={stat}>
              {consolidatedData.statistics[stat.toLowerCase()].map((row: any, rowIndex: number) => (
                <TableRow key={`${stat}-${rowIndex}`} className={"bg-gray-100"}>
                  {rowIndex === 0 && (
                    <TableCell rowSpan={consolidatedData.statistics[stat.toLowerCase()].length} className="border border-gray-300 text-center p-2 font-semibold">
                      {stat}
                    </TableCell>
                  )}
                  <TableCell className="border border-gray-300 text-center p-2">{row.palletType}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", (variant === 'postShip' || variant==='postStack') && stat !== 'Count' && parseFloat(row['A-B']) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row['A-B']}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", (variant === 'postShip' || variant==='postStack') && stat !== 'Count' && parseFloat(row['B-C']) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row['B-C']}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", (variant === 'postShip' || variant==='postStack') && stat !== 'Count' && parseFloat(row['C-D']) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row['C-D']}</TableCell>
                  <TableCell className={cn("border border-gray-300 text-center p-2", (variant === 'postShip' || variant==='postStack') && stat !== 'Count' && parseFloat(row['D-A']) > 1 ? "bg-bgRed text-white font-bold" : "")}>{row['D-A']}</TableCell>
                </TableRow>
              ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      <Table className="border-collapse w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Row Labels</TableHead>
            <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Data Fill Status</TableHead>
            {(variant === 'postShip' || variant==='postStack') && <>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">Overall pass/fail status</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">A-B Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">B-C Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">C-D Pass Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A Fail Count</TableHead>
              <TableHead className="border border-gray-300 text-center bg-gray-100 p-2 text-center font-semibold">D-A Pass Count</TableHead>
            </>
            }
          </TableRow>
        </TableHeader>
        <TableBody>
          {consolidatedData.additionalStats.map((row: any, index: number) => (
            <TableRow key={index} className={"bg-gray-100"}>
              <TableCell className="border border-gray-300 text-center p-2">{row.rowLabel}</TableCell>
              <TableCell className="border border-gray-300 text-center p-2">{row.dataFillStatus}</TableCell>
              {(variant === 'postShip' || variant==='postStack') && <>
                <TableCell className={`${row.overallStatus === 'Fail' ? "text-white bg-bgRed" : row.overallStatus === 'Pass' ? " text-white bg-states-success" : ""} border border-gray-300 text-center p-2`}>{row.overallStatus}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['A-BFailCount']) > 0 ? "bg-bgRed text-white" : "bg-states-success text-white")}>{row['A-BFailCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['A-BPassCount']) > 0 ? "bg-states-success text-white" : "bg-bgRed text-white  ")}>{row['A-BPassCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['B-CFailCount']) > 0 ? "bg-bgRed text-white" : "bg-states-success text-white  ")}>{row['B-CFailCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['B-CPassCount']) > 0 ? "bg-states-success text-white" : "bg-bgRed text-white    ")}>{row['B-CPassCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['C-DFailCount']) > 0 ? "bg-bgRed text-white" : "bg-states-success text-white")}>{row['C-DFailCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['C-DPassCount']) > 0 ? "bg-states-success text-white" : "bg-bgRed text-white    ")}>{row['C-DPassCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['D-AFailCount']) > 0 ? "bg-bgRed text-white" : "bg-states-success text-white")}>{row['D-AFailCount']}</TableCell>
                <TableCell className={cn("border border-gray-300 text-center p-2", parseInt(row['D-APassCount']) > 0 ? "bg-states-success text-white" : "bg-bgRed text-white")}>{row['D-APassCount']}</TableCell>
              </>}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
  const renderGraph = () => {
    const graphData = consolidatedData.statistics[activeStatistic].map((item: any) => ({
      name: item.palletType,
      'A-B': parseFloat(item['A-B']),
      'B-C': parseFloat(item['B-C']),
      'C-D': parseFloat(item['C-D']),
      'D-A': parseFloat(item['D-A']),
    }));

    const colors = {
      'A-B': "#004899",
      'B-C': "#82ca9d",
      'C-D': "#ffc658",
      'D-A': "#ff7300"
    };

    return (
      <div className="space-y-4">
        <div className="flex space-x-2">
          {['max', 'min', 'average', 'stdev'].map((stat) => (
            <Button
              key={stat}
              onClick={() => setActiveStatistic(stat)}
              className={cn(
                "px-4 justify-start hover:bg-blue hover:text-white",
                activeStatistic === stat ? "bg-blue text-white" : "bg-[#FAFAFA] text-gray-700"
              )}
            >
              {stat.charAt(0).toUpperCase() + stat.slice(1)}
            </Button>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={graphData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar barSize={20} dataKey="A-B" fill={colors['A-B']} />
            <Bar barSize={20} dataKey="B-C" fill={colors['B-C']} />
            <Bar barSize={20} dataKey="C-D" fill={colors['C-D']} />
            <Bar barSize={20} dataKey="D-A" fill={colors['D-A']} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  return (

    <div className="flex-1 p-6 overflow-auto">

      {renderStatisticsTable()}

    </div>

  );
};

export default SummaryStackUnitLoadHeight;